import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import WelcomeKitChoice from '@/components/form/WelcomeKitChoice'
import Button from '@/components/Button'
import TitleBar from '@/components/TitleBar'
import AddressCollectionModal from '@/components/AddressCollectionModal'
import { getPendingWelcomeKits } from '@/hooks/useWelcomeKit'
import { isPendingWelcomeKit } from '@/lib/welcomeKit'
import { purchaseWelcomeKit } from '@/hooks/useStripe'
import { getUserFromClerkId } from '@/hooks/useUser'
import { useUser } from "@clerk/clerk-react";

export const Route = createFileRoute('/_auth/request-welcome-kit')({
  component: RouteComponent,
})

function RouteComponent() {
  const [selected, setSelected] = useState<string | null>(null)
  const { user: clerkUser } = useUser();
  const userQuery = clerkUser ? getUserFromClerkId(clerkUser.id) : { isLoading: false, data: undefined };
  const purchaseKitMutation = purchaseWelcomeKit()
  const navigate = Route.useNavigate()
  const searchParams = Route.useSearch();
  const invitationcode = searchParams.invitationcode;
  const pendingKitsQuery = getPendingWelcomeKits()
  const [kitOrdered, setKitOrdered] = useState(false)
  const [showAddressModal, setShowAddressModal] = useState(false)

  const canSubmit = !!selected

  const handleKitChange = (val: string) => {
    setSelected(val)
  }

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!canSubmit || !clerkUser?.id) return;
    try {
      const result = await purchaseKitMutation.mutateAsync({ returnTo: '/_auth/request-welcome-kit' });
      if (result && typeof result === 'object' && 'redirectUrl' in result && typeof result.redirectUrl === 'string') {
        window.location.assign(result.redirectUrl);
      } else if (result && 'success' in result && result.success) {
        setKitOrdered(true);
        setTimeout(() => navigate({ to: '/', search: { invitationcode, __clerk_ticket: undefined } }), 1000);
      }
    } catch (err: any) {
      // Optionally handle error state
      // setError(err?.message || 'Failed to start purchase');
    }
  }

  if (userQuery.isLoading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-white py-8">
        <TitleBar title="Welcome Kit Request" />
        <div className="text-lg mt-8">Loading your information...</div>
      </div>
    );
  }

  if (userQuery.data?.onboardingProgress?.requestKit) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-white py-8">
        <TitleBar title="Welcome Kit Request" />
        <div className="text-lg mt-8 font-semibold">Your welcome kit has already been ordered.</div>
        <div className="text-base mt-4 text-gray-700 text-center max-w-lg">
          If you need to re-order or have any questions, please contact our support team at
          <a href="mailto:support@granguard.org" className="text-blue-600 underline ml-1">support@granguard.org</a>.
        </div>
        <Button size="large" variant="secondary" className="mt-8" onClick={() => navigate({ to: '/', search: { invitationcode, __clerk_ticket: undefined } })}>
          Return Home
        </Button>
      </div>
    );
  }

  if (purchaseKitMutation.isPending) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-white py-8">
        <TitleBar title="Welcome Kit Request" />
        <div className="text-lg mt-8">Submitting your request...</div>
      </div>
    )
  }

  if (purchaseKitMutation.isError) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-white py-8">
        <TitleBar title="Welcome Kit Request" />
        <div className="text-lg mt-8 text-red-600">There was an error submitting your request. Please try again.</div>
      </div>
    )
  }

  if (pendingKitsQuery.isLoading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-white py-8">
        <TitleBar title="Welcome Kit Request" />
        <div className="text-lg mt-8">Checking your welcome kit status...</div>
      </div>
    );
  }

  if (pendingKitsQuery.isError) {
    console.error(pendingKitsQuery.error);
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-white py-8">
        <TitleBar title="Welcome Kit Request" />
        <div className="text-lg mt-8 text-red-600">Unable to check your welcome kit status. Please try again later.</div>
      </div>
    );
  }

  if (isPendingWelcomeKit(pendingKitsQuery.data)) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-white py-8">
        <TitleBar title="Welcome Kit Request" />
        <div className="text-lg mt-8 font-semibold">
          You have a starter kit ready to ship!
        </div>
        <div className="text-base mt-4 text-gray-700 text-center max-w-lg">
          When you are ready to receive your kit, click below to enter your delivery details and initiate delivery.
        </div>
        <Button size="large" variant="primary" className="mt-8" onClick={() => setShowAddressModal(true)}>
          Enter Delivery Details &amp; Initiate Delivery
        </Button>
        <AddressCollectionModal
          show={showAddressModal}
          onClose={() => setShowAddressModal(false)}
        />
      </div>
    );
  }

  if (kitOrdered) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-white py-8">
        <TitleBar title="Welcome Kit Request" />
        <div className="text-lg mt-8 font-semibold">Welcome kit ordered, redirecting to home...</div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center bg-white py-8">
      <TitleBar title="Request a Welcome Kit">
        <p className="text-lg mb-0" style={{ color: 'var(--color-tertiary)' }}>
          Choose digital, physical, or both. We’ll collect your shipping address later when you’re ready to send it.
        </p>
      </TitleBar>
      <form className="w-full flex flex-col items-center" onSubmit={handleSubmit}>
        <WelcomeKitChoice value={selected} onChange={handleKitChange} />
        {/* No address collection here; it happens later during the send flow */}
        <Button type="submit" size="large" variant="primary" className="w-full max-w-md mt-8" disabled={!canSubmit}>
          Submit Request
        </Button>
      </form>
    </div>
  )
}
